import {
  ApplicationConfig,
  inject,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';

import { HTTP_INTERCEPTORS_IN_ORDER } from '@core/interceptors';

import { DialogService } from '@core/dialog/dialog.service';
import { ToastService } from '@core/notification/toast.service';
import {
  LISTING_CONFIRM,
  LISTING_NOTIFY,
  type ListingConfirmFn,
  type ListingNotifyFn
} from '@shared/components/listing/listing.tokens';

import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),

    // Angular 21 runs without zone.js. There is no automatic change detection
    // triggered by async work, so ALL mutable component state must live in
    // signals (or go through markForCheck) or the view will not update.
    provideZonelessChangeDetection(),

    provideRouter(
      routes,
      // Route params/data flow straight into matching input() members.
      withComponentInputBinding(),
      // Always land at the top of a freshly navigated page.
      withInMemoryScrolling({ scrollPositionRestoration: 'top' })
    ),

    provideHttpClient(
      withFetch(),
      // Order is load-bearing and lives in the barrel, not here. Spread is required:
      // the export is a readonly array.
      withInterceptors([...HTTP_INTERCEPTORS_IN_ORDER])
    ),

    // The listing package ships deliberately dependency-free: its confirm and notify hooks
    // default to window.confirm and a no-op so it never imports @core. Wiring them to the
    // real dialog and toast services is the composition root's job, and this is it.
    {
      provide: LISTING_CONFIRM,
      useFactory: (): ListingConfirmFn => {
        const dialog = inject(DialogService);
        return (confirm) =>
          dialog.confirm({
            title: confirm.title,
            message: confirm.message,
            confirmLabel: confirm.confirmLabel,
            tone: confirm.tone
          });
      }
    },
    {
      provide: LISTING_NOTIFY,
      useFactory: (): ListingNotifyFn => {
        const toast = inject(ToastService);
        return (message, tone) =>
          tone === 'success' ? toast.success('Done', message) : toast.error('Failed', message);
      }
    }

    // TODO(WP-31): override LISTING_PERMISSION_CHECK with the JWT-claim lookup. Until then
    // an ActionConfig.permission is decorative, which is safe only because no feature
    // screen exists yet.
  ]
};
